import { motion } from "framer-motion";
import { CHAPTERS, IMAGES } from "@/lib/data";
import { MaskedLines, FadeUp } from "@/components/Reveal";

const Manifesto = () => {
  return (
    <section id="manifesto" className="relative bg-[#FAFAF8] py-24 lg:py-40" data-testid="manifesto-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Sticky heading + image */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <p className="eyebrow text-[#C89933] mb-6">The Approach</p>
              <h2 className="font-serif-display text-[#0F1E3D] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                <MaskedLines lines={["We turn financial", "friction into", "quiet clarity."]} />
              </h2>

              <FadeUp delay={0.2} className="mt-10 relative w-full aspect-[4/5] max-w-sm overflow-hidden">
                <motion.img
                  src={IMAGES.manifesto}
                  alt="Structural marble texture"
                  className="h-full w-full object-cover"
                  initial={{ scale: 1.2 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-[#0F1E3D]/10" />
              </FadeUp>
            </div>
          </div>

          {/* Numbered chapters */}
          <div className="lg:col-span-7">
            <div className="flex flex-col">
              {CHAPTERS.map((c, i) => (
                <motion.div
                  key={c.no}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  className={`py-10 lg:py-14 ${i !== 0 ? "border-t border-[#E2E8F0]" : ""}`}
                  data-testid={`chapter-${c.no}`}
                >
                  <div className="flex items-start gap-6 lg:gap-10">
                    <span className="font-serif-display text-[#C89933]/70 text-3xl lg:text-4xl leading-none pt-1 tabular-nums">
                      {c.no}
                    </span>
                    <div>
                      <h3 className="font-serif-display text-[#0F1E3D] text-2xl lg:text-4xl tracking-tight mb-4">
                        {c.title}
                      </h3>
                      <p className="text-[#4A5568] text-base lg:text-lg leading-relaxed font-light max-w-xl">
                        {c.body}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Mission / Vision */}
        <div className="mt-24 lg:mt-36 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
          {[
            { label: "Mission", text: "Give clients a clear picture of their wealth today, and turn their financial goals into reality." },
            { label: "Vision", text: "To become a partner that transforms how families navigate wealth, so every client moves from financial awareness to generational prosperity." },
          ].map((item) => (
            <FadeUp key={item.label} className="bg-[#FAFAF8] p-10 lg:p-16">
              <p className="overline text-[#7F8D76] mb-6">{item.label}</p>
              <p className="font-serif-display text-[#0F1E3D] text-2xl lg:text-3xl leading-snug tracking-tight">
                {item.text}
              </p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
